<template>
  <div class="visualization-content pa-4">
    <v-card outlined elevation="2" class="fact-chart-panel excel-graph-panel">
      <v-card-title class="visualization-title">
        <v-icon left color="#8E44AD">mdi-chart-areaspline</v-icon>
        Excel Graph
        <v-spacer></v-spacer>
        <v-progress-circular
          v-if="isLoading"
          indeterminate
          size="22"
          width="2"
          color="primary"
        />
      </v-card-title>

      <v-card-text>
        <v-alert v-if="error" type="warning" outlined dense class="mb-4">
          {{ error }}
        </v-alert>

        <div class="excel-graph-grid">
          <div class="fact-price-panel">
            <div class="fact-price-panel-header">
              <div>
                <div class="chart-title-line">
                  <h4>{{ activeTable.title }}</h4>
                  <source-tooltip-icon :enabled="showParameterSourceTooltips" :text="activeTable.source_range" />
                </div>
                <span class="chart-note-chip">{{ activeTable.subtitle }}</span>
                <span class="chart-note-chip">{{ activeTable.unit }}</span>
              </div>
              <year-toggle v-model="tableYear" />
            </div>
            <div class="excel-graph-table-wrap">
              <table class="excel-graph-table excel-graph-cost-table">
                <thead>
                  <tr class="excel-graph-unit-row">
                    <th></th>
                    <th :colspan="mainTableColumns.length">{{ activeTable.unit || 'US$/vehicle' }}</th>
                    <th :colspan="percentTableColumns.length" class="percent-boundary">% of BAU values</th>
                  </tr>
                  <tr>
                    <th>Vehicle</th>
                    <th v-for="column in mainTableColumns" :key="column.key">{{ column.label }}</th>
                    <th
                      v-for="(column, index) in percentTableColumns"
                      :key="column.key"
                      :class="{ 'percent-boundary': index === 0 }"
                    >
                      {{ column.label }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in activeTable.rows" :key="row.vehicle">
                    <th>{{ row.vehicle }}</th>
                    <td v-for="column in mainTableColumns" :key="column.key" :class="{ negative: row[column.key] < 0 }">
                      {{ formatTableValue(row[column.key], column.key) }}
                    </td>
                    <td
                      v-for="(column, index) in percentTableColumns"
                      :key="column.key"
                      :class="{ negative: row[column.key] < 0, 'percent-boundary': index === 0 }"
                    >
                      {{ formatTableValue(row[column.key], column.key) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="fact-price-panel">
            <div class="fact-price-panel-header">
              <div class="chart-title-line">
                <h4>{{ activeInvestmentSeries.title || 'Breakdown of investment needs' }}</h4>
                <source-tooltip-icon :enabled="showParameterSourceTooltips" :text="activeInvestmentSeries.source_range" />
                <span class="chart-note-chip">{{ activeInvestmentSeries.unit || 'm$' }}</span>
              </div>
              <year-toggle v-model="investmentYear" />
            </div>
            <div class="excel-graph-chart-wrap">
              <canvas ref="investmentPieChart"></canvas>
            </div>
          </div>
        </div>

        <v-divider class="my-6" />

        <div class="fact-chart-controls excel-graph-inline-controls">
          <div class="fact-chart-control-block">
            <div class="fact-chart-control-label">Year</div>
            <year-toggle v-model="costYear" />
          </div>
        </div>
        <div class="excel-graph-grid excel-graph-grid-two">
          <chart-card
            title="Cost Advantage by Vehicle Type - 4 Types"
            :unit="activeCostFourSeries.unit"
            :source-range="activeCostFourSeries.source_range"
          >
            <canvas ref="costFourChart"></canvas>
          </chart-card>
          <chart-card
            title="Cost Advantage by Vehicle Type - 6 Types"
            :unit="activeCostSixSeries.unit"
            :source-range="activeCostSixSeries.source_range"
          >
            <canvas ref="costSixChart"></canvas>
          </chart-card>
        </div>

        <v-divider class="my-6" />

        <div class="fact-chart-controls excel-graph-inline-controls">
          <div class="fact-chart-control-block">
            <div class="fact-chart-control-label">Year</div>
            <year-toggle v-model="driversYear" />
          </div>
        </div>
        <chart-card title="Drivers of Cost Advantage" :unit="activeDriversSeries.unit" :source-range="activeDriversSeries.source_range">
          <canvas ref="driversChart"></canvas>
        </chart-card>

        <v-divider class="my-6" />

        <div class="fact-chart-controls excel-graph-inline-controls">
          <div class="fact-chart-control-block">
            <div class="fact-chart-control-label">Year</div>
            <year-toggle v-model="externalitiesYear" />
          </div>
        </div>
        <div class="excel-graph-grid excel-graph-grid-two">
          <chart-card
            title="Externalities Advantage - 4 Types"
            :unit="activeExternalitiesFourSeries.unit"
            :source-range="activeExternalitiesFourSeries.source_range"
          >
            <canvas ref="externalitiesFourChart"></canvas>
          </chart-card>
          <chart-card
            title="Externalities Advantage - 6 Types"
            :unit="activeExternalitiesSixSeries.unit"
            :source-range="activeExternalitiesSixSeries.source_range"
          >
            <canvas ref="externalitiesSixChart"></canvas>
          </chart-card>
        </div>

        <v-divider class="my-6" />

        <div class="fact-chart-controls excel-graph-inline-controls">
          <div class="fact-chart-control-block">
            <div class="fact-chart-control-label">TCO vehicle</div>
            <v-btn-toggle v-model="activeTcoVehicle" mandatory dense color="primary" class="fact-chart-toggle">
              <v-btn v-for="vehicle in tcoVehicleOptions" :key="vehicle.value" :value="vehicle.value" small>
                {{ vehicle.text }}
              </v-btn>
            </v-btn-toggle>
          </div>
        </div>
        <chart-card
          :title="activeTcoSeries.title || 'Total Cost of Ownership by Type'"
          :unit="activeTcoSeries.unit"
          :source-range="activeTcoSeries.source_range"
        >
          <canvas ref="tcoChart"></canvas>
        </chart-card>

        <v-divider class="my-6" />

        <div class="fact-chart-controls excel-graph-inline-controls">
          <div class="fact-chart-control-block">
            <div class="fact-chart-control-label">Year</div>
            <year-toggle v-model="fiscalYear" />
          </div>
        </div>
        <div class="excel-graph-grid excel-graph-grid-two">
          <chart-card title="Net Fiscal Impact" :unit="activeFiscalImpactSeries.unit" :source-range="activeFiscalImpactSeries.source_range">
            <canvas ref="fiscalImpactChart"></canvas>
          </chart-card>
          <chart-card
            :title="activeFiscalInvestmentSeries.title || 'Breakdown of investment needs'"
            :unit="activeFiscalInvestmentSeries.unit || 'm$'"
            :source-range="activeFiscalInvestmentSeries.source_range"
          >
            <canvas ref="fiscalInvestmentPieChart"></canvas>
          </chart-card>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import ChartJS from './chartSetup'
import {
  DEFAULT_CHART_COLOR,
  PIE_CHART_COLORS,
  getTcoCostColor,
  getVehicleTypeColor
} from './colorPalette'
import SourceTooltipIcon from './SourceTooltipIcon.vue'
import { SHOW_PARAMETER_SOURCE_TOOLTIPS } from './sourceTooltipConfig'

const COMPONENT_COLORS = {
  'Vehicle Capital': '#C94374',
  Fuel: '#35BDB2',
  Maintenance: '#A6BC18',
  'Charging Infrastructure': '#2F80ED',
  Externalities: '#7F8C8D',
  'Local Externalities (NOx, SOx, PM2.5)': '#A6BC18',
  'Global Externalities (CO2)': '#C7D43A',
  'Vehicle Taxes & Subsidies': '#C94374',
  'Vehicle Duties': '#35BDB2',
  'Petrol tax': '#A6BC18',
  'Diesel tax': '#1F9DD6',
  'Eletricity tax': '#7F8C8D'
}

export default {
  name: 'ExcelGraphTab',
  components: {
    SourceTooltipIcon,
    YearToggle: {
      functional: true,
      model: {
        prop: 'value',
        event: 'input'
      },
      props: { value: String },
      render(h, context) {
        return h('div', { class: 'excel-graph-year-toggle' }, ['2030', '2035'].map(year => h('button', {
          class: { active: context.props.value === year },
          attrs: { type: 'button' },
          on: { click: () => context.listeners.input(year) }
        }, year)))
      }
    },
      ChartCard: {
        functional: true,
        props: { title: String, unit: String, sourceRange: String },
        render(h, context) {
          return h('div', { class: 'fact-price-panel' }, [
            h('div', { class: 'fact-price-panel-header' }, [
              h('div', { class: 'chart-title-line' }, [
                h('h4', context.props.title),
                SHOW_PARAMETER_SOURCE_TOOLTIPS && context.props.sourceRange
                  ? h(SourceTooltipIcon, {
                    props: {
                      enabled: SHOW_PARAMETER_SOURCE_TOOLTIPS,
                      text: context.props.sourceRange
                    }
                  })
                  : null,
                context.props.unit ? h('span', { class: 'chart-note-chip' }, context.props.unit) : null
              ])
            ]),
            h('div', { class: 'excel-graph-chart-wrap' }, context.children)
          ])
      }
    }
  },
  props: {
    domain: {
      type: String,
      required: true
    },
    analysisResult: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      excelGraphData: null,
      error: '',
      isLoading: false,
      tableYear: '2030',
      investmentYear: '2030',
      costYear: '2030',
      driversYear: '2030',
      externalitiesYear: '2030',
      fiscalYear: '2030',
      activeTcoVehicle: 'car',
      chartInstances: {},
      mainTableColumns: [
        { key: 'vehicle_capital_cost', label: 'Capital' },
        { key: 'charging_infrastructure', label: 'Charging' },
        { key: 'vehicle_operating_cost', label: 'Oper.' },
        { key: 'sub_total', label: 'Sub-total' },
        { key: 'local_externality', label: 'Local Ext.' },
        { key: 'global_externality', label: 'Global Ext.' },
        { key: 'economic_advantage', label: 'Econ.' },
        { key: 'fiscal_wedge', label: 'Fiscal Wedge' },
        { key: 'financial_advantage', label: 'Fin.' },
        { key: 'fuel', label: 'Fuel' },
        { key: 'maintenance', label: 'Maint.' },
        { key: 'battery', label: 'Batt.' }
      ],
      percentTableColumns: [
        { key: 'economic_advantage_pct', label: 'Econ. %' },
        { key: 'financial_advantage_pct', label: 'Fin. %' }
      ],
      tcoVehicleOptions: [
        { value: 'car', text: 'Cars' },
        { value: '2w', text: '2W' },
        { value: '3w', text: '3W' },
        { value: 'bus', text: 'Bus' },
        { value: 'lcv', text: 'LCV' },
        { value: 'hdv', text: 'HDV' }
      ]
    }
  },
  computed: {
    showParameterSourceTooltips() {
      return SHOW_PARAMETER_SOURCE_TOOLTIPS
    },
    activeTable() {
      return this.excelGraphData && this.excelGraphData.tables
        ? this.excelGraphData.tables[this.tableYear] || { rows: [], columns: [] }
        : { rows: [], columns: [] }
    },
    activeInvestmentSeries() {
      return this.getYearSeries('investment_needs', this.investmentYear)
    },
    activeFiscalInvestmentSeries() {
      return this.getYearSeries('fiscal_investment_needs', this.fiscalYear)
    },
    activeCostFourSeries() {
      return this.getNestedYearSeries('cost_advantage', 'four_types', this.costYear)
    },
    activeCostSixSeries() {
      return this.getNestedYearSeries('cost_advantage', 'six_types', this.costYear)
    },
    activeDriversSeries() {
      return this.getYearSeries('drivers', this.driversYear)
    },
    activeExternalitiesFourSeries() {
      return this.getNestedYearSeries('externalities', 'four_types', this.externalitiesYear)
    },
    activeExternalitiesSixSeries() {
      return this.getNestedYearSeries('externalities', 'six_types', this.externalitiesYear)
    },
    activeFiscalImpactSeries() {
      return this.getYearSeries('fiscal_impact', this.fiscalYear)
    },
    activeTcoSeries() {
      return this.excelGraphData && this.excelGraphData.tco
        ? this.excelGraphData.tco[this.activeTcoVehicle] || { labels: [], datasets: [] }
        : { labels: [], datasets: [] }
    }
  },
  watch: {
    analysisResult() {
      this.fetchExcelGraphData()
    },
    investmentYear() {
      this.$nextTick(this.renderInvestmentCharts)
    },
    costYear() {
      this.$nextTick(this.renderCostCharts)
    },
    driversYear() {
      this.$nextTick(this.renderDriversChart)
    },
    externalitiesYear() {
      this.$nextTick(this.renderExternalitiesCharts)
    },
    fiscalYear() {
      this.$nextTick(this.renderFiscalCharts)
    },
    activeTcoVehicle() {
      this.$nextTick(this.renderTcoChart)
    }
  },
  mounted() {
    this.fetchExcelGraphData()
  },
  beforeDestroy() {
    Object.values(this.chartInstances).forEach(chart => {
      if (chart) chart.destroy()
    })
  },
  methods: {
    getPreloadedExcelGraphData() {
      return this.analysisResult && this.analysisResult.preloaded_outputs
        ? this.analysisResult.preloaded_outputs.excel_graph || null
        : null
    },
    async fetchExcelGraphData() {
      const requestedFileName = this.analysisResult ? this.analysisResult.file_name : null
      const preloadedData = this.getPreloadedExcelGraphData()
      try {
        this.isLoading = true
        this.error = ''
        if (preloadedData) {
          console.log('[Output Read] Excel Graph:', preloadedData.file_name || requestedFileName || 'preloaded data')
          this.$emit('file-read', {
            section: 'excel_graph',
            sectionTitle: 'Excel Graph',
            fileName: preloadedData.file_name || requestedFileName
          })
          this.excelGraphData = preloadedData
          this.$nextTick(this.renderAllCharts)
          return
        }
        this.$emit('loading-state', {
          section: 'excel_graph',
          sectionTitle: 'Excel Graph',
          fileName: requestedFileName,
          isLoading: true
        })
        const response = await fetch(this.domain + '/zev/excel_graph_data', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            file_name: requestedFileName
          })
        })
        const data = await response.json()
        if (!response.ok) {
          throw new Error(data.detail || data.error || `HTTP error! status: ${response.status}`)
        }
        if (data.error) throw new Error(data.error)
        this.excelGraphData = data
        console.log('[Output Read] Excel Graph:', data.file_name || requestedFileName || 'latest analysis workbook')
        this.$emit('file-read', {
          section: 'excel_graph',
          sectionTitle: 'Excel Graph',
          fileName: data.file_name || requestedFileName
        })
        this.$nextTick(this.renderAllCharts)
      } catch (error) {
        console.error('Error loading Excel Graph data:', error)
        this.error = error.message
        this.excelGraphData = null
      } finally {
        this.isLoading = false
        this.$emit('loading-state', {
          section: 'excel_graph',
          sectionTitle: 'Excel Graph',
          fileName: requestedFileName,
          isLoading: false
        })
      }
    },
    getYearSeries(key, year) {
      return this.excelGraphData && this.excelGraphData[key]
        ? this.excelGraphData[key][year] || { items: [] }
        : { items: [] }
    },
    renderAllCharts() {
      this.renderInvestmentCharts()
      this.renderCostCharts()
      this.renderDriversChart()
      this.renderExternalitiesCharts()
      this.renderTcoChart()
      this.renderFiscalCharts()
    },
    renderInvestmentCharts() {
      this.renderPieChart('investmentPieChart', this.activeInvestmentSeries)
    },
    renderCostCharts() {
      this.renderGroupedBarChart('costFourChart', this.activeCostFourSeries, { percent: true })
      this.renderGroupedBarChart('costSixChart', this.activeCostSixSeries, { percent: true })
    },
    renderDriversChart() {
      this.renderStackedBarChart('driversChart', this.activeDriversSeries, { percent: true, stackedAxis: true })
    },
    renderExternalitiesCharts() {
      this.renderGroupedBarChart('externalitiesFourChart', this.activeExternalitiesFourSeries, { percent: true })
      this.renderGroupedBarChart('externalitiesSixChart', this.activeExternalitiesSixSeries, { percent: true })
    },
    renderFiscalCharts() {
      this.renderStackedBarChart('fiscalImpactChart', this.activeFiscalImpactSeries, { money: true })
      this.renderPieChart('fiscalInvestmentPieChart', this.activeFiscalInvestmentSeries)
    },
    getNestedYearSeries(group, key, year) {
      return this.excelGraphData && this.excelGraphData[group] && this.excelGraphData[group][key]
        ? this.excelGraphData[group][key][year] || { labels: [], datasets: [] }
        : { labels: [], datasets: [] }
    },
    renderTcoChart() {
      this.renderStackedBarChart('tcoChart', this.activeTcoSeries, { money: true })
    },
    destroyChart(refName) {
      if (this.chartInstances[refName]) {
        this.chartInstances[refName].destroy()
        this.chartInstances[refName] = null
      }
      const canvas = this.$refs[refName]
      if (canvas && ChartJS.getChart) {
        const existing = ChartJS.getChart(canvas)
        if (existing) existing.destroy()
      }
    },
    renderPieChart(refName, series) {
      const canvas = this.$refs[refName]
      if (!canvas) return
      this.destroyChart(refName)
      const items = series.items || []
      if (!items.length) return
      this.chartInstances[refName] = new ChartJS(canvas.getContext('2d'), {
        type: 'pie',
        data: {
          labels: items.map(item => item.label),
          datasets: [{
            data: items.map(item => item.value),
            backgroundColor: items.map((_, index) => PIE_CHART_COLORS[index % PIE_CHART_COLORS.length]),
            borderColor: '#fff',
            borderWidth: 2
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: 'right' },
            tooltip: { callbacks: { label: context => `${context.label}: ${this.formatNumber(context.parsed)} ${series.unit || ''}` } }
          }
        }
      })
    },
    renderGroupedBarChart(refName, series, options = {}) {
      const canvas = this.$refs[refName]
      if (!canvas) return
      this.destroyChart(refName)
      if (!series || !series.datasets || !series.datasets.length) return
      this.chartInstances[refName] = new ChartJS(canvas.getContext('2d'), {
        type: 'bar',
        data: {
          labels: series.labels || [],
          datasets: series.datasets.map((dataset, index) => ({
            label: dataset.label,
            data: dataset.values,
            backgroundColor: COMPONENT_COLORS[dataset.label] || PIE_CHART_COLORS[index % PIE_CHART_COLORS.length]
          }))
        },
        options: this.getBarOptions(options)
      })
    },
    renderStackedBarChart(refName, series, options = {}) {
      const canvas = this.$refs[refName]
      if (!canvas) return
      this.destroyChart(refName)
      if (!series || !series.datasets || !series.datasets.length) return
      this.chartInstances[refName] = new ChartJS(canvas.getContext('2d'), {
        type: 'bar',
        data: {
          labels: series.labels || [],
          datasets: series.datasets.map((dataset, index) => ({
            label: dataset.label,
            data: dataset.values,
            backgroundColor: this.getSeriesColor(dataset.label, index)
          }))
        },
        options: this.getBarOptions({ ...options, stacked: true })
      })
    },
    getBarOptions(options) {
      return {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { position: 'bottom' },
          tooltip: {
            callbacks: {
              label: context => `${context.dataset.label}: ${this.formatChartValue(context.parsed.y, options)}`
            }
          }
        },
        scales: {
          x: {
            stacked: !!options.stacked,
            grid: { display: false },
            ticks: {
              maxRotation: options.stackedAxis ? 90 : 0,
              minRotation: options.stackedAxis ? 90 : 0
            }
          },
          y: {
            stacked: !!options.stacked,
            ticks: { callback: value => this.formatChartValue(value, options) }
          }
        }
      }
    },
    getSeriesColor(label, index) {
      const tcoColor = getTcoCostColor(label)
      if (tcoColor !== DEFAULT_CHART_COLOR) return tcoColor
      const vehicleColor = getVehicleTypeColor(label)
      if (vehicleColor !== DEFAULT_CHART_COLOR) return vehicleColor
      return COMPONENT_COLORS[label] || PIE_CHART_COLORS[index % PIE_CHART_COLORS.length]
    },
    formatChartValue(value, options) {
      if (value === null || value === undefined || value === '') return '-'
      if (options.percent) return `${Number(value).toFixed(1)}%`
      return this.formatNumber(value)
    },
    formatTableValue(value, key) {
      if (value === null || value === undefined || value === '') return '-'
      if (key.includes('_pct')) return `${Number(value).toFixed(1)}%`
      return this.formatNumber(value)
    },
    formatNumber(value) {
      if (value === null || value === undefined || value === '') return '-'
      const number = Number(value)
      if (!Number.isFinite(number)) return '-'
      return Math.round(number).toLocaleString()
    }
  }
}
</script>
